import { useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectSelectedElement,
  selectSequenceVariables,
  addSequenceVariable,
  updateSequenceVariable,
  deleteSequenceVariable,
} from "../../../app/slices/TrainingSlice";
import Interaction, {
  InteractionType,
} from "../../../models/interactions/Interaction";
import {
  Variable,
  duplicateVariable,
} from "../../../models/interactions/Variable";
import { isBuiltInVariable } from "../../../models/interactions/BuiltInSequenceVariables";
import PiButton from "../../../components/PiButton";
import AddIcon from "@mui/icons-material/Add";
import VariableItem from "./VariableItem";
import NewVariableForm from "./NewVariableForm";

interface SequenceVariablesManagerProps {
  includeBuiltIns?: boolean;
  showSectionHeaders?: boolean;
}

export default function SequenceVariablesManager({
  includeBuiltIns = true,
  showSectionHeaders = true,
}: SequenceVariablesManagerProps) {
  const dispatch = useDispatch();
  const selectedElement = useSelector(selectSelectedElement);
  const variables = useSelector(selectSequenceVariables);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  // Ensure we have a sequence selected
  const isSequence =
    selectedElement &&
    (selectedElement as Interaction).type === InteractionType.Sequence;

  const handleAdd = () => {
    setIsAdding(true);
  };

  const handleSaveNew = (variable: Variable) => {
    dispatch(addSequenceVariable(variable));
    setIsAdding(false);
  };

  const handleCancel = () => {
    setEditingId(null);
    setIsAdding(false);
  };

  const handleEdit = (variableId: string) => {
    const variableToEdit = variables.find((v) => v.id === variableId);
    if (!variableToEdit || isBuiltInVariable(variableToEdit)) {
      return;
    }
    setEditingId(variableId);
  };

  const handleSave = (variable: Variable) => {
    dispatch(
      updateSequenceVariable({
        variableId: variable.id,
        variable,
      })
    );
    setEditingId(null);
  };

  const handleDelete = (variableId: string) => {
    const variableToDelete = variables.find((v) => v.id === variableId);
    if (!variableToDelete || isBuiltInVariable(variableToDelete)) {
      return;
    }

    if (
      window.confirm(
        "Are you sure you want to delete this variable? This may break references in nodes."
      )
    ) {
      dispatch(deleteSequenceVariable(variableId));
    }
  };

  const handleDuplicate = (variableId: string) => {
    const variableToDuplicate = variables.find((v) => v.id === variableId);
    if (variableToDuplicate && !isBuiltInVariable(variableToDuplicate)) {
      const duplicatedVariable = duplicateVariable(
        variableToDuplicate,
        variables
      );
      dispatch(addSequenceVariable(duplicatedVariable));
    }
  };

  const builtInVariables = useMemo(() => {
    if (!includeBuiltIns) return [];
    return variables.filter((variable) => isBuiltInVariable(variable));
  }, [variables, includeBuiltIns]);
  const customVariables = useMemo(
    () => variables.filter((variable) => !isBuiltInVariable(variable)),
    [variables]
  );

  const renderVariableItems = (list: Variable[]) =>
    list.map((variable) => (
      <VariableItem
        key={variable.id}
        variable={variable}
        isEditing={editingId === variable.id}
        onEdit={() => handleEdit(variable.id)}
        onSave={handleSave}
        onCancel={handleCancel}
        onDelete={() => handleDelete(variable.id)}
        onDuplicate={() => handleDuplicate(variable.id)}
        existingVariables={variables}
      />
    ));

  // Early return after all hooks
  if (!isSequence) {
    return null;
  }

  const isEditing = editingId !== null;
  const showVariableList = !isAdding && !isEditing;
  const showAddButton = !isAdding && !isEditing;
  const showEmptyState = customVariables.length === 0 && !isAdding && !isEditing;
  const editingVariable = isEditing
    ? variables.find((v) => v.id === editingId)
    : null;

  return (
    <div className="flex flex-col gap-2 w-full h-full font-poppins">
      {showVariableList && (
        <div className="flex flex-col gap-3">
          {builtInVariables.length > 0 && (
            <div className="flex flex-col gap-1">
              {showSectionHeaders && (
                <div className="px-2 text-[11px] uppercase tracking-wide text-creator-text-soft font-poppins">
                  Built-in Variables
                </div>
              )}
              {renderVariableItems(builtInVariables)}
            </div>
          )}
          <div className="flex flex-col gap-1">
            {showSectionHeaders && (
              <div className="px-2 text-[11px] uppercase tracking-wide text-creator-text-soft font-poppins">
                Custom Variables
              </div>
            )}
            {renderVariableItems(customVariables)}
          </div>
        </div>
      )}
      {isAdding && (
        <NewVariableForm
          onSave={handleSaveNew}
          onCancel={handleCancel}
          existingVariables={variables}
        />
      )}
      {isEditing && editingVariable && (
        <VariableItem
          variable={editingVariable}
          isEditing={true}
          onEdit={() => {}}
          onSave={handleSave}
          onCancel={handleCancel}
          onDelete={() => {}}
          onDuplicate={() => {}}
          existingVariables={variables}
        />
      )}
      {showAddButton && (
        <div className="w-full h-[36px]">
          <PiButton variant="piBlueOutlined" onClick={handleAdd}>
            <div className="flex gap-2 justify-center items-center">
              <AddIcon fontSize="small" />
              <span>Add Variable</span>
            </div>
          </PiButton>
        </div>
      )}
      {showEmptyState && (
        <div className="text-[12px] text-creator-text-sub text-center py-4 font-poppins">
          No custom variables defined. Click "Add Variable" to create one.
        </div>
      )}
    </div>
  );
}
